import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Popper,
  Paper,
  Box,
  useTheme,
  ClickAwayListener,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItemText,
  Divider,
  Collapse,
  ListItemButton,
  ListItemIcon,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MenuIcon from "@mui/icons-material/Menu";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import navbarLogo from "../../assets/images/navbar/Navbar.png";
import productIcon from "../../assets/images/navbar/icons-product.gif";
import initiativesIcon from "../../assets/images/navbar/icons-initiatives.gif";
import aboutIcon from "../../assets/images/navbar/icons-about.gif";
import researchIcon from "../../assets/images/navbar/icons-research.gif";
import donateIcon from "../../assets/images/navbar/icons-donate.gif";
import contactIcon from "../../assets/images/navbar/icons-contact.gif";
import questIcon from "../../assets/images/navbar/icons-quest.gif";
import conceptAiIcon from "../../assets/images/navbar/icons-concept-ai.gif";
import estimatingTruthIcon from "../../assets/images/navbar/icons-estimating.gif";
import wikipediaIcon from "../../assets/images/navbar/icons-wikipedia.gif";
import psychicIcon from "../../assets/images/navbar/icons-psychic.gif";
import closeIcon from "../../assets/images/navbar/icons-close.gif";
import loginIcon from "../../assets/images/navbar/icons-loginout.gif";

interface DropdownItem {
  name: string;
  path: string;
  icon: string;
}

interface DropdownMenu {
  label: string;
  link: string;
  icon: string;
  items: DropdownItem[];
}

const Navbar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:1330px)");
  const productsRef = useRef<HTMLDivElement>(null!);
  const initiativesRef = useRef<HTMLDivElement>(null!);

  const [openMenu, setOpenMenu] = useState<"products" | "initiatives" | null>(
    null
  );
  const [userExists, setUserExists] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const refMap: Record<string, React.RefObject<HTMLDivElement>> = {
    Products: productsRef,
    Initiatives: initiativesRef,
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const checkUser = () => {
      const user = localStorage.getItem("user");
      setUserExists(!!user && user !== "null" && user !== "undefined");
    };

    checkUser();
    window.addEventListener("storage", checkUser);
    return () => window.removeEventListener("storage", checkUser);
  }, []);

  const handleNav = useCallback(
    (path: string) => {
      navigate(path);
      setOpenMenu(null);
      setDrawerOpen(false);
    },
    [navigate]
  );

  const dropdownMenus: DropdownMenu[] = [
    {
      label: "Products",
      link: "/products",
      icon: productIcon,
      items: [
        { name: "Quest", path: "/products/quest", icon: questIcon },
        {
          name: "Concept AI",
          path: "/products/concept-ai",
          icon: conceptAiIcon,
        },
        {
          name: "Quest AI Runner",
          path: "/products/quest-ai-runner",
          icon: productIcon,
        },
      ],
    },
    {
      label: "Initiatives",
      link: "/initiatives",
      icon: initiativesIcon,
      items: [
        {
          name: "Estimating Truth",
          path: "/initiatives/estimating-truth",
          icon: estimatingTruthIcon,
        },
        {
          name: "Wikipedia Advocacy",
          path: "/initiatives/wikipedia-advocacy",
          icon: wikipediaIcon,
        },
        {
          name: "Psychic Ability Certification",
          path: "/initiatives/psychic-ability-certification",
          icon: psychicIcon,
        },
      ],
    },
  ];

  const staticLinks = [
    { label: "Research", path: "/research", icon: researchIcon },
    { label: "Donate", path: "/donate", icon: donateIcon },
    { label: "About Us", path: "/about-us", icon: aboutIcon },
    { label: "Contact", path: "/contact", icon: contactIcon },
  ];

  // Helper function to check if a path is active
  const isActivePath = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  // Function to get nav link styles based on active state
  const getNavLinkStyles = (isActive: boolean) => ({
    fontSize: "14px",
    fontWeight: 600,
    textTransform: "uppercase" as const,
    fontFamily: "Poppins, sans-serif",
    letterSpacing: "0.5px",
    cursor: "pointer",
    px: 1.5,
    py: 1,
    transition: "all 0.3s ease",
    color: isActive ? (theme.palette.primary.focus || "#FFBF00") : "#F2F3EB",
    "&:hover": { color: theme.palette.primary.focus || "#FFBF00" },
  });

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: theme.palette.primary.hover,
          boxShadow: `0px 2px 12px rgba(0,0,0,0.2)`,
          transition: "all 0.3s ease",
          px: { xs: 2, md: 20 },
          py: 1.5,
          fontFamily: "Poppins, sans-serif",
        }}
      >
        <Toolbar
          disableGutters
          sx={{ justifyContent: "space-between", flexWrap: "wrap" }}
        >
          {isMobile ? (
            <>
              {/* Mobile: Left = Hamburger */}
              <IconButton
                onClick={() => setDrawerOpen(true)}
                edge="start"
                sx={{ color: "#F2F3EB" }}
              >
                <MenuIcon />
              </IconButton>

              {/* Mobile: Center = Logo */}
              <Box
                onClick={() => handleNav("/")}
                sx={{ cursor: "pointer", mx: "auto", pt:1 }}
              >
                <Box
                  component="img"
                  src={navbarLogo}
                  alt="Logo"
                  sx={{ height: { xs: 40, md: 50 }, objectFit: "contain", mixBlendMode: "lighten" }}
                />
              </Box>

              {/* Mobile: Right = Sign In */}
              <Button
                onClick={() => {
                  window.open("https://quest.spiritualdata.org/sign-in", "_blank");
                }}
                sx={{
                  backgroundColor: theme.palette.primary.focus,
                  color: theme.palette.primary.hover,
                  borderRadius: 8,
                  height: 42,
                  px: 3,
                  fontWeight: 700,
                  fontSize: "14px",
                  textTransform: "uppercase",
                  fontFamily: "Poppins, sans-serif",
                  letterSpacing: "0.5px",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#F2F3EB",
                    color: theme.palette.primary.hover,
                  },
                }}
              >
                Sign In
              </Button>
            </>
          ) : (
            <>
              {/* Desktop Navbar */}
              <Box onClick={() => handleNav("/")} sx={{ cursor: "pointer", pt:0.8 }}>
                <Box
                  component="img"
                  src={navbarLogo}
                  alt="Logo"
                  sx={{ height: 54, objectFit: "contain", mixBlendMode: "lighten" }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1.2, md: 1.8 },
                }}
              >
                {dropdownMenus.map((menu) => (
                  <Box
                    key={menu.label}
                    ref={refMap[menu.label]}
                    onMouseEnter={() =>
                      setOpenMenu(menu.label.toLowerCase() as any)
                    }
                    onMouseLeave={() => setOpenMenu(null)}
                    sx={{ position: "relative" }}
                  >
                    <Box
                      onClick={() => handleNav(menu.link)}
                      sx={[
                        getNavLinkStyles(isActivePath(menu.link)),
                        {
                          display: "flex",
                          alignItems: "center",
                        },
                      ]}
                      aria-haspopup="true"
                    >
                      {menu.label}
                      <ArrowDropDownIcon sx={{ ml: 0.5, fontSize: "20px" }} />
                    </Box>
                    <Popper
                      open={openMenu === menu.label.toLowerCase()}
                      anchorEl={refMap[menu.label].current}
                      placement="bottom-start"
                      style={{ zIndex: 1200 }}
                    >
                      <ClickAwayListener onClickAway={() => setOpenMenu(null)}>
                        <Paper
                          elevation={2}
                          sx={{
                            mt: 1,
                            borderRadius: 1.5,
                            backgroundColor: "#F2F3EB",
                            py: 1,
                            minWidth: 220,
                          }}
                        >
                          {menu.items.map((item) => (
                            <Box
                              key={item.name}
                              onClick={() => handleNav(item.path)}
                              sx={{
                                px: 3,
                                py: 1.2,
                                fontSize: "13px",
                                fontWeight: 500,
                                fontFamily: "Poppins, sans-serif",
                                cursor: "pointer",
                                color: theme.palette.text.primary,
                                letterSpacing: "0.5px",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  color: theme.palette.primary.focus,
                                  backgroundColor: "transparent",
                                },
                              }}
                            >
                              {item.name}
                            </Box>
                          ))}
                        </Paper>
                      </ClickAwayListener>
                    </Popper>
                  </Box>
                ))}
                {staticLinks.map(({ label, path }) => (
                  <Box
                    key={label}
                    onClick={() => handleNav(path)}
                    sx={getNavLinkStyles(isActivePath(path))}
                  >
                    {label}
                  </Box>
                ))}
                <Button
                  onClick={() => {
                    window.open("https://quest.spiritualdata.org/sign-in", "_blank");
                  }}
                  sx={{
                    backgroundColor: theme.palette.primary.focus,
                    color: theme.palette.primary.hover,
                    borderRadius: 8,
                    height: 42,
                    px: 4,
                    fontWeight: 700,
                    fontSize: "14px",
                    textTransform: "uppercase",
                    fontFamily: "Poppins, sans-serif",
                    letterSpacing: "0.5px",
                    ml: 2,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "#F2F3EB",
                      color: theme.palette.primary.hover,
                    },
                  }}
                >
                  Sign In
                </Button>
              </Box>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Drawer for Mobile */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg,  ${theme.palette.primary.focus} 0%,${theme.palette.primary.main} 29%,${theme.palette.primary.main} 87%,${theme.palette.primary.focus} 100%)`,
          },
        }}
      >
        <Box
          onClick={() => handleNav("/")}
          sx={{ cursor: "pointer", mx: "auto", mt: 4 }}
        >
          <Box
            onClick={() => setDrawerOpen(false)}
            component="img"
            src={closeIcon}
            alt="close"
            sx={{
              height: 20,
              objectFit: "contain",
              position: "absolute",
              top: 10,
              right: 10,
            }}
          />
          <Box
            component="img"
            src={navbarLogo}
            alt="Logo"
            sx={{ height: { xs: 40, md: 50 }, objectFit: "contain" }}
          />
        </Box>

        <Box
          width={300}
          role="presentation"
          sx={{
            mt: 2,
            fontFamily: "Sansation, sans-serif",
            px: { xs: 2, sm: 2 },
          }}
        >
          <List>
            {/* Dropdown Menus */}
            {dropdownMenus.map((menu) => {
              const isParentActive =
                isActivePath(menu.link) ||
                menu.items.some((item) => isActivePath(item.path));
              return (
                <>
                  <Box key={menu.label}>
                    <ListItemButton
                      onClick={() =>
                        setExpanded(expanded === menu.label ? null : menu.label)
                      }
                      sx={{
                        backgroundColor: isParentActive
                          ? theme.palette.primary.focus + "40"
                          : "transparent",
                        borderLeft: isParentActive
                          ? `4px solid ${theme.palette.primary.focus}`
                          : "4px solid transparent",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.focus + "20",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: "40px" }}>
                        <Box
                          component="img"
                          src={menu.icon}
                          alt={menu.label}
                          sx={{ height: 25, objectFit: "contain" }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={menu.label}
                        sx={{
                          color: isParentActive
                            ? theme.palette.primary.hover
                            : "black",
                          fontWeight: isParentActive ? 700 : 400,
                        }}
                      />
                      {expanded === menu.label ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>

                  {/* Submenus */}
                  <Collapse
                    in={expanded === menu.label}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding>
                      {menu.items.map((item) => (
                        <ListItemButton
                          key={item.name}
                          sx={{
                            pl: 6,
                            backgroundColor: isActivePath(item.path)
                              ? theme.palette.primary.focus + "40"
                              : "transparent",
                            borderLeft: isActivePath(item.path)
                              ? `4px solid ${theme.palette.primary.focus}`
                              : "4px solid transparent",
                            "&:hover": {
                              backgroundColor: theme.palette.primary.focus + "20",
                            },
                          }}
                          onClick={() => handleNav(item.path)}
                        >
                          <ListItemIcon sx={{ minWidth: "40px" }}>
                            <Box
                              component="img"
                              src={item.icon}
                              alt={item.name}
                              sx={{ height: 25, objectFit: "contain" }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={item.name}
                            sx={{
                              color: isActivePath(item.path)
                                ? theme.palette.primary.hover
                                : "black",
                              fontWeight: isActivePath(item.path) ? 700 : 400,
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </Box>
                <Divider />
              </>
            );
            })}

            {/* Static Links */}
            {staticLinks.map(({ label, path, icon }) => (
              <ListItemButton
                key={label}
                onClick={() => handleNav(path)}
                sx={{
                  backgroundColor: isActivePath(path)
                    ? theme.palette.primary.focus + "40"
                    : "transparent",
                  borderLeft: isActivePath(path)
                    ? `4px solid ${theme.palette.primary.focus}`
                    : "4px solid transparent",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.focus + "20",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: "40px" }}>
                  <Box
                    component="img"
                    src={icon}
                    alt={label}
                    sx={{ height: 25, objectFit: "contain" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  sx={{
                    color: isActivePath(path) ? theme.palette.primary.hover : "black",
                    fontWeight: isActivePath(path) ? 700 : 400,
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>

        <Box
          sx={{
            mx: "auto",
            my: 4,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <Divider sx={{ width: "100%", mb: 2 }} />
          <Button
            onClick={() => {
              if (userExists) {
                handleNav("/sign-out");
              } else {
                window.open("https://quest.spiritualdata.org", "_blank");
              }
            }}
            sx={{
              background: theme.palette.primary.focus,
              color: theme.palette.primary.hero,
              borderRadius: 3,
              width: 280,
              height: 50,
              px: 3,
              fontWeight: 900,
              border: "1px solid rgba(0, 0, 0, 0.14)",
              fontSize: "14px",
              fontFamily: "sansation-bold, sans-serif",
              letterSpacing: "1px",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: theme.palette.primary.hero,
                color: theme.palette.primary.focus,
              },
            }}
          >
            <Box
              component="img"
              src={loginIcon}
              alt={userExists ? "Sign Out" : "Sign In"}
              sx={{ height: 25, objectFit: "contain", mr: 1 }}
            />
            {userExists ? "Sign Out" : "Sign In"}
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
